import React, { useRef } from 'react';
import { Button } from '../atoms/Button';
import { Dialog, DialogDescription, DialogTitle } from './Dialog';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
  isLoading = false,
  returnFocusRef,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const buttonVariant = variant === 'danger' ? 'danger' : variant === 'warning' ? 'secondary' : 'primary';

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      // A pending confirmation must not be cancelled out from under the running operation.
      dismissible={!isLoading}
      busy={isLoading}
      initialFocusRef={cancelRef}
      returnFocusRef={returnFocusRef}
      className="z-50 flex items-center justify-center"
    >
      {/* Dialog panel */}
      <div className="relative w-full max-w-lg mx-4 rounded-lg bg-slate-800 border border-slate-700 shadow-xl overflow-hidden">
        <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white">{title}</DialogTitle>
              <div className="mt-2">
                <DialogDescription className="text-sm text-slate-400">{message}</DialogDescription>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 border-t border-slate-700 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          <Button
            type="button"
            variant={buttonVariant}
            onClick={onConfirm}
            isLoading={isLoading}
            className="w-full sm:ml-3 sm:w-auto"
          >
            {confirmLabel}
          </Button>
          <Button
            ref={cancelRef}
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="mt-3 w-full sm:mt-0 sm:w-auto"
          >
            {cancelLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
